import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { connect } from "react-redux";
import { motion } from "framer-motion";
import { X, UserPlus } from "lucide-react";
import { useGoogleLogin, TokenResponse } from "@react-oauth/google";
import ModalOverlay from "../ModalOverlay/ModalOverlay.tsx";
import {
  logoutFromGuestAccount,
  closeSuscriptionModal,
  loginWithGoogle,
} from "../../../store/actions/";
import { fireAnalytics } from "../../../utils/firebase";
import styles from "./GuestAccountModal.module.scss";

const modalVariants = {
  hidden: { opacity: 0, scale: 1.1, y: 20 },
  visible: {
    opacity: 1,
    scale: 1.0,
    y: 0,
    transition: { delay: 0.1, duration: 0.3, ease: "easeOut" },
  },
};

interface GuestAccountModalProps {
  closeSuscriptionModal: () => void;
  logoutFromGuestAccount: (navigate: ReturnType<typeof useNavigate> | null) => any;
  loginWithGoogle: (token: string, navigate: ReturnType<typeof useNavigate>) => any;
}

const GuestAccountModal: React.FC<GuestAccountModalProps> = ({
  closeSuscriptionModal,
  logoutFromGuestAccount,
  loginWithGoogle,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isDashboard = location.pathname === "/dashboard";

  const login = useGoogleLogin({
    onSuccess: (tokenResponse: TokenResponse) => {
      fireAnalytics("guest_modal_google_click", { category: "authentication" });
      if (tokenResponse.access_token) {
        (loginWithGoogle as any)(tokenResponse.access_token, navigate);
      }
    },
    onError: () => {
      fireAnalytics("guest_modal_google_error", { category: "authentication" });
    },
    scope: "openid email profile",
  });

  const handleGoogle = () => {
    closeSuscriptionModal();
    (logoutFromGuestAccount as any)(null);
    login();
  };

  const handleRegisterWithEmail = () => {
    closeSuscriptionModal();
    (logoutFromGuestAccount as any)(navigate);
  };

  const handleClose = () => {
    if (!isDashboard) {
      navigate("/dashboard");
    }
    closeSuscriptionModal();
  };

  return (
    <ModalOverlay isOpen={true} onClose={handleClose}>
      <motion.div
        className={styles.modal}
        variants={modalVariants}
        initial="hidden"
        animate="visible"
        exit="hidden"
      >
        <div className={styles.modalBody}>
          <button
            className={styles.closeButton}
            onClick={handleClose}
            aria-label="Cerrar modal"
          >
            <X size={20} />
          </button>

          <div className={styles.content}>
            <div className={styles.iconContainer}>
              <UserPlus size={48} className={styles.icon} />
            </div>

            <h3 className={styles.title}>Cuenta de invitado</h3>
            <p className={styles.bodyText}>
              Estás usando una cuenta de invitado para explorar la plataforma.
              Para acceder a todas las funcionalidades necesitás crear una cuenta.
            </p>
            <div className={styles.ctaBlock}>
              <p className={styles.ctaTitle}>Crear cuenta es gratis</p>
              <ul className={styles.ctaBenefits}>
                <li>60 días de prueba gratuita</li>
                <li>Interactuá con la IA de Edify de forma ilimitada</li>
                <li>No se necesita tarjeta de crédito</li>
                <li>Creala en segundos con Google o con tu email</li>
              </ul>
            </div>
          </div>

          <div className={styles.options}>
            <button
              className={styles.googleButton}
              onClick={handleGoogle}
              type="button"
            >
              <div className={styles.googleButton_content}>
                <svg
                  className={styles.googleButton_icon}
                  viewBox="0 0 24 24"
                  width="24"
                  height="24"
                >
                  <path
                    fill="currentColor"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="currentColor"
                    opacity="0.9"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="currentColor"
                    opacity="0.7"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="currentColor"
                    opacity="0.9"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
                <span className={styles.googleButton_text}>
                  Continuar con Google
                </span>
              </div>
            </button>
            <button
              className={styles.primaryButton}
              onClick={handleRegisterWithEmail}
              type="button"
            >
              Crear cuenta con email
            </button>
            <button
              className={styles.linkCta}
              onClick={handleClose}
              type="button"
            >
              {isDashboard ? "Seguir explorando" : "Volver al dashboard"}
            </button>
          </div>
        </div>
      </motion.div>
    </ModalOverlay>
  );
};

export default connect(null, {
  closeSuscriptionModal,
  logoutFromGuestAccount,
  loginWithGoogle,
})(GuestAccountModal);
